import { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { postApiAccountsLogin } from './api/accounts-client';

// Technically there should be an interface representing all clients, but it generates the same so it hackily works
import { ApiRequestOptions } from './api/listings-client/core/ApiRequestOptions';
import { OpenAPI as ListingsOpenAPI } from './api/listings-client';
import { OpenAPI as MessagesOpenAPI } from './api/messages-client';
import { OpenAPI as MediaOpenAPI } from './api/media-client';

export interface AuthUser {
    userId: string;
    userName: string;
    userEmail: string;
    profilePictureUrl: string;
}

export interface TokenableAPI {
    TOKEN?: string | ((options: ApiRequestOptions) => Promise<string>);
    interceptors: {
        request: {
            use: (fn: (config: RequestInit) => RequestInit | Promise<RequestInit>) => void;
        };
    };
}

const initializeAPI = (api: any): TokenableAPI => {
    if (!api.interceptors) {
        api.interceptors = {
            request: {
                use: (fn: (config: RequestInit) => RequestInit | Promise<RequestInit>) => {
                    api._requestInterceptor = fn;
                }
            }
        };
    }
    return api;
};

export const defaultApiClients: TokenableAPI[] = [
    ListingsOpenAPI,
    MessagesOpenAPI,
    MediaOpenAPI,
].map(initializeAPI);

interface AuthContextType {
    token: string | null;
    user: AuthUser | null;
    fetchToken: (googleToken?: string) => Promise<void>;
    logout: () => void;
    isLoading: boolean;
    error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: React.ReactNode;
    apiClients: TokenableAPI[];
}

export function AuthProvider({ children, apiClients }: AuthProviderProps) {
    const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Setup API interceptors
    useEffect(() => {
        apiClients.forEach(client => {
            client.interceptors.request.use(async (config: RequestInit) => {
                if (token) {
                    const existingHeaders = config.headers instanceof Headers
                        ? config.headers
                        : new Headers(config.headers);

                    let headerRecord: Record<string, string> = {};
                    existingHeaders.forEach((value, key) => {
                        if (key.toLowerCase() !== 'authorization') {
                            headerRecord[key] = value;
                        }
                    });

                    headerRecord['Authorization'] = `Bearer ${token}`;
                    return {
                        ...config,
                        headers: new Headers(headerRecord)
                    };
                }
                return config;
            });
        });
    }, [token, apiClients]);

    // Parse token and set user data when token changes
    useEffect(() => {
        if (token) {
            try {
                const decoded = jwtDecode<{
                    sub: string;
                    userName: string;
                    email: string;
                    exp: number;
                }>(token);

                const profilePictureUrl = decoded.sub
                    ? `https://static.propyn.com/users/${decoded.sub}/profile_pic.png`
                    : '';

                setUser({
                    userId: decoded.sub,
                    userName: decoded.userName,
                    userEmail: decoded.email,
                    profilePictureUrl
                });
            } catch (error) {
                console.error('Error decoding token:', error);
                logout();
            }
        } else {
            setUser(null);
        }
    }, [token]);

    const fetchToken = async (googleToken?: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await postApiAccountsLogin({
                requestBody: { googleToken }
            });

            setToken(response.token);
            localStorage.setItem('authToken', response.token);
        } catch (error) {
            console.error('Login error:', error);
            setError('Login Failed');
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('authToken');
    };

    return (
        <AuthContext.Provider
            value={{
                token,
                user,
                fetchToken,
                logout,
                isLoading,
                error
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
